// Microsoft Clarity session context (the tag is loaded in public/index.html).
//
// - identify: the Shopify customer id, so a support ticket's member can be
//   found in Clarity under Filters → Custom filters → Custom user ID. Clarity
//   hashes the id in the browser before sending it; we pass no email and no
//   friendly name, so nothing personal reaches Clarity in clear text.
// - portal_design tag: "new" or "old", from the redesign assignment, so every
//   Clarity filter (and crash count) can be split by design.
// - build tag: the git SHA the bundle was built from (REACT_APP_BUILD_SHA,
//   set from Heroku's SOURCE_VERSION in heroku-postbuild), so a minified stack
//   in Clarity maps to a commit.
//
// Everything here is best-effort: Clarity may be blocked, not loaded yet, or
// absent in tests, and none of that may ever affect the member.
import { isTestVersionActive, REDESIGN_VERSION_EVENT } from './redesignExperiment';

const callClarity = (...args) => {
  try {
    if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
      window.clarity(...args);
      return true;
    }
  } catch (e) {
    // never let analytics throw
  }
  return false;
};

// Shopify ids arrive as "gid://shopify/Customer/123" or "123"; Clarity gets "123".
export const normalizeShopifyCustomerId = (value) => {
  if (value === null || value === undefined) return null;
  const last = String(value).trim().split('/').pop();
  return /^\d+$/.test(last) ? last : null;
};

let identifiedAs = null;

export const identifyMemberInClarity = (shopifyCustomerId) => {
  const id = normalizeShopifyCustomerId(shopifyCustomerId);
  if (!id || id === identifiedAs) return false;
  const sent = callClarity('identify', id);
  if (sent) identifiedAs = id;
  return sent;
};

export const resetClarityIdentity = () => {
  identifiedAs = null;
};

export const currentPortalDesign = () => (isTestVersionActive() ? 'new' : 'old');

export const tagPortalDesign = () => callClarity('set', 'portal_design', currentPortalDesign());

export const buildSha = () => {
  const sha = process.env.REACT_APP_BUILD_SHA;
  return sha ? String(sha).slice(0, 12) : null;
};

export const tagBuild = () => {
  const sha = buildSha();
  return sha ? callClarity('set', 'build', sha) : false;
};

// Called once from index.js. Tags are session-scoped in Clarity, so setting
// them at boot (and again whenever the design assignment changes) is enough.
export const initClarityTags = () => {
  if (typeof window === 'undefined') return;
  tagBuild();
  tagPortalDesign();
  window.addEventListener(REDESIGN_VERSION_EVENT, tagPortalDesign);
};
